import { Component, computed, inject, input } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import type { OnyxColor } from '@onyx/angular';
import { OnyxInformationHeadingComponent, OnyxNamePipe } from '@onyx/angular';
import { chain, orderBy } from 'lodash';
import { RouteHelper } from '../../../../../../../../common/helpers/route.helper';
import type { TelematicsRoutePoint } from '../../../../../../../../common/interfaces/telematics/telematics-route-point';
import type { TelematicsRoutePointOrder } from '../../../../../../../../common/interfaces/telematics/telematics-route-point-order';
import { FleetIdentifierPipe } from '../../../../../../../../common/pipes/fleet-identifier.pipe';
import { OrderHelper } from '../../../../../../../../dashboard/orders/common/helpers/order.helper';
import { DriverHelper } from '../../../../../../../drivers/common/helpers/driver.helper';
import type { SimplifiedDriver } from '../../../../../../../drivers/common/interfaces/driver';
import { FleetCategory } from '../../../../../../../fleet/common/enums/fleet-category';
import { FleetHelper } from '../../../../../../../fleet/common/helpers/fleet.helper';
import type { SimplifiedFleet } from '../../../../../../../fleet/common/interfaces/fleet';
import { OrderPointCategory } from '../../../../../../../orders/common/enums/order-point-category';
import { MapSwapPanelDetailsSectionSwapPointItemComponent } from '../../../../../../map/map-swap-panel/map-swap-panel-details/map-swap-panel-details-section/map-swap-panel-details-section-swap-point/map-swap-panel-details-section-swap-point-item/map-swap-panel-details-section-swap-point-item.component';
import { SwapSetAction } from '../../../../../enums/swap/swap-set-action';
import { SwapVehicleType } from '../../../../../enums/swap/swap-vehicle-type';
import { SwapDetailsHelper } from '../../../../../helpers/swap/swap-details.helper';
import { SwapHelper } from '../../../../../helpers/swap/swap.helper';
import type { Swap } from '../../../../../interfaces/swap/swap';
import type { SwapSetData } from '../../../../../interfaces/swap/swap-set';

type ModalSwapDriverItem =
  | { type: 'driver'; data: SwapSetData<SimplifiedDriver> }
  | { type: 'placeholder'; placeholder: string };

type ModalSwapTrailerItem =
  | {
      type: 'trailer';
      data: SwapSetData<SimplifiedFleet>;
      initialTrailer?: SimplifiedFleet | null;
    }
  | { type: 'dropped'; trailer: SimplifiedFleet }
  | { type: 'placeholder'; placeholder: string };

@Component({
  selector: 'app-swap-modal-summary-item-details',
  imports: [
    OnyxInformationHeadingComponent,
    TranslatePipe,
    MapSwapPanelDetailsSectionSwapPointItemComponent,
    OnyxNamePipe,
    FleetIdentifierPipe,
  ],
  templateUrl: './swap-modal-summary-item-details.component.html',
  styleUrl: './swap-modal-summary-item-details.component.scss',
})
export class SwapModalSummaryItemDetailsComponent {
  private readonly fleetHelper = inject(FleetHelper);
  private readonly driverHelper = inject(DriverHelper);
  private readonly orderHelper = inject(OrderHelper);

  public readonly type = input.required<SwapVehicleType>();
  public readonly swap = input.required<Swap>();
  public readonly identifierColor = input.required<OnyxColor | null>();

  protected readonly I18N = 'map.swapPanel.swapModal';

  protected readonly SwapSetAction = SwapSetAction;
  protected readonly SwapHelper = SwapHelper;
  protected readonly SwapDetailsHelper = SwapDetailsHelper;

  protected isPrimary = computed(() => this.type() === SwapVehicleType.PRIMARY);
  protected vehicle = computed(() =>
    this.isPrimary() ? this.swap().vehicle : this.swap().otherVehicle,
  );
  protected initialSet = computed(() =>
    this.isPrimary() ? this.swap().initialSet : this.swap().otherInitialSet,
  );
  protected currentSet = computed(() =>
    this.isPrimary() ? this.swap().vehicleSet : this.swap().otherVehicleSet,
  );
  protected initialOtherSet = computed(() =>
    this.isPrimary() ? this.swap().otherInitialSet : this.swap().initialSet,
  );
  protected currentOtherSet = computed(() =>
    this.isPrimary() ? this.swap().otherVehicleSet : this.swap().vehicleSet,
  );

  protected trailers = computed<ModalSwapTrailerItem[]>(() => {
    const before = this.initialSet()?.trailer;
    const after = this.currentSet()?.trailer;
    const otherBefore = this.initialOtherSet()?.trailer;
    const otherAfter = this.currentOtherSet()?.trailer;

    const isTransferredToOther = !!before && otherAfter?.uuid === before.uuid;
    const isTransferredFromOther = !!after && otherBefore?.uuid === after.uuid;
    const isSwapped = isTransferredToOther && isTransferredFromOther;
    const isDropped =
      !!before &&
      (!after || after.uuid !== before.uuid) &&
      !isTransferredToOther;

    const items: ModalSwapTrailerItem[] = [];
    if (isDropped) items.push({ type: 'dropped', trailer: before });

    if (after) {
      items.push({
        type: 'trailer',
        data: {
          value: after,
          action: isSwapped
            ? SwapSetAction.SWAP
            : isTransferredFromOther
              ? SwapSetAction.TRANSFER
              : SwapSetAction.NONE,
          isManuallyAdded:
            before?.uuid !== after.uuid && !isTransferredFromOther,
        },
        initialTrailer: isSwapped ? before : null,
      });
    } else {
      items.push({
        type: 'placeholder',
        placeholder:
          this.vehicle()?.generalInformation?.category ===
          FleetCategory.SEMI_TRUCK
            ? 'map.swapPanel.noSemiTrailer'
            : 'map.swapPanel.noTrailer',
      });
    }

    return items;
  });

  protected drivers = computed<ModalSwapDriverItem[]>(() => {
    const getDrivers = (set: Swap['vehicleSet'] | null) =>
      chain([set?.primaryDriver, set?.secondaryDriver]).compact().value();

    const before = getDrivers(this.initialSet());
    const after = getDrivers(this.currentSet());
    const otherBefore = getDrivers(this.initialOtherSet());
    const otherAfter = getDrivers(this.currentOtherSet());

    const activeItems = after.map((currentDriver): ModalSwapDriverItem => {
      const isTransferred = otherBefore.some(
        (driver) => driver.uuid === currentDriver.uuid,
      );

      return {
        type: 'driver',
        data: {
          value: currentDriver,
          action: isTransferred ? SwapSetAction.TRANSFER : SwapSetAction.NONE,
          isManuallyAdded:
            !isTransferred &&
            !before.some((driver) => driver.uuid === currentDriver.uuid),
        },
      };
    });

    const droppedItems = before
      .filter(
        (initialDriver) =>
          !after.some((driver) => driver.uuid === initialDriver.uuid) &&
          !otherAfter.some((driver) => driver.uuid === initialDriver.uuid),
      )
      .map((initialDriver): ModalSwapDriverItem => ({
        type: 'driver',
        data: {
          value: initialDriver,
          action: SwapSetAction.DROP_OFF_IN_POINT,
        },
      }));

    const placeholders = [
      'map.swapPanel.noPrimaryDriver',
      'map.swapPanel.noSecondaryDriver',
    ]
      .slice(activeItems.length)
      .map((placeholder): ModalSwapDriverItem => ({
        type: 'placeholder',
        placeholder,
      }));

    return [...activeItems, ...placeholders, ...droppedItems];
  });

  protected activeOrders = computed(() => {
    const afterRoute = this.isPrimary()
      ? this.swap().vehicleRoute
      : (this.swap().otherVehicleRoute ?? []);
    const otherBeforeRoute = this.isPrimary()
      ? (this.swap().initialOtherVehicleRoute ?? [])
      : this.swap().initialVehicleRoute;

    const afterUnassignedPoints = this.isPrimary()
      ? this.swap().vehicleUnassignedPoints
      : (this.swap().otherVehicleUnassignedPoints ?? []);
    const afterTrailerPoints = this.isPrimary()
      ? this.swap().vehicleSemiTrailerPoints
      : (this.swap().otherVehicleSemiTrailerPoints ?? []);

    const otherBeforeOrderUuids = new Set(
      otherBeforeRoute.map(({ order }) => order?.uuid),
    );
    const afterUnassignedOrderUuids = new Set(
      afterUnassignedPoints.map(({ order }) => order?.uuid),
    );
    const afterTrailerOrderUuids = new Set(
      afterTrailerPoints.map(({ order }) => order?.uuid),
    );

    const orders = [
      ...otherBeforeRoute,
      ...afterUnassignedPoints,
      ...afterTrailerPoints,
    ]
      .map((point) => point.order)
      .filter((order): order is TelematicsRoutePointOrder => !!order);

    const afterActiveOrders = this.getActiveOrders(
      afterRoute,
      this.swap().uuid,
      afterTrailerOrderUuids,
      afterUnassignedOrderUuids,
      otherBeforeOrderUuids,
      orders,
    );

    const items = new Map<string, SwapSetData<TelematicsRoutePointOrder>>();

    const setItem = (
      order: TelematicsRoutePointOrder,
      action: SwapSetAction,
    ): void => {
      if (items.has(order.uuid)) return;

      items.set(order.uuid, {
        value: order,
        action,
      });
    };

    for (const order of afterActiveOrders) {
      let action = SwapSetAction.NONE;

      if (afterTrailerOrderUuids.has(order.uuid)) {
        action = SwapSetAction.DROP_OFF_ON_SEMI_TRAILER;
      } else if (afterUnassignedOrderUuids.has(order.uuid)) {
        action = SwapSetAction.UNLOAD_IN_POINT;
      } else if (otherBeforeOrderUuids.has(order.uuid)) {
        action = SwapSetAction.TRANSFER;
      }

      setItem(order, action);
    }

    const getActionPriority = (action: SwapSetAction): number => {
      switch (action) {
        case SwapSetAction.UNLOAD_IN_POINT:
          return 2;
        case SwapSetAction.DROP_OFF_ON_SEMI_TRAILER:
          return 3;
        case SwapSetAction.TRANSFER:
          return 4;
        default:
          return 1;
      }
    };

    return orderBy(
      Array.from(items.values()),
      (item) => getActionPriority(item.action),
      'asc',
    );
  });

  protected openFleetModal(fleet: SimplifiedFleet): void {
    if (fleet) this.fleetHelper.openModal(fleet);
  }

  protected openDriverModal(driver: SimplifiedDriver | null): void {
    if (driver) this.driverHelper.openModal(driver);
  }

  protected openOrderModal(order: TelematicsRoutePointOrder | null): void {
    if (order) this.orderHelper.openModal(order);
  }

  protected trackTrailer(item: ModalSwapTrailerItem, index: number): string {
    if (item.type === 'trailer') {
      return `trailer-${item.data.value?.uuid ?? index}`;
    }
    if (item.type === 'dropped') return `dropped-${item.trailer.uuid}`;

    return `placeholder-${item.placeholder}`;
  }

  protected trackDriver(item: ModalSwapDriverItem, index: number): string {
    if (item.type === 'driver') {
      return `driver-${item.data.value?.uuid ?? index}`;
    }

    return `placeholder-${item.placeholder}`;
  }

  protected trackOrder(
    item: SwapSetData<TelematicsRoutePointOrder>,
    index: number,
  ): string {
    return `order-${item.value?.uuid ?? index}-${item.action}`;
  }

  private getActiveOrders(
    route: TelematicsRoutePoint[] | null,
    swapUuid: string,
    trailerUuids: Set<string | undefined>,
    unassignedUuids: Set<string | undefined>,
    otherBeforeOrderUuids: Set<string | undefined>,
    orders: TelematicsRoutePointOrder[],
  ): TelematicsRoutePointOrder[] {
    if (!route?.length) return [];

    const swapPointIndex = route.findIndex(
      (point) => RouteHelper.getSwapData(point)?.uuid === swapUuid,
    );
    if (swapPointIndex === -1) return [];

    const loadingBeforeSwapUuids = new Set<string>();
    const unloadingAfterSwapUuids = new Set<string>();
    const ordersMap = new Map<string, TelematicsRoutePointOrder>();

    for (const order of orders) ordersMap.set(order.uuid, order);

    for (const [index, point] of route.entries()) {
      if (point.technicalPoint || !point.order) continue;

      const uuid = point.order.uuid;
      ordersMap.set(uuid, point.order);

      if (
        point.point.category === OrderPointCategory.LOADING &&
        index < swapPointIndex
      ) {
        loadingBeforeSwapUuids.add(uuid);
      }

      if (
        point.point.category === OrderPointCategory.UNLOADING &&
        index > swapPointIndex
      ) {
        unloadingAfterSwapUuids.add(uuid);
      }
    }

    const activeOrders: TelematicsRoutePointOrder[] = [];
    const allLoadedBeforeUuids = new Set([
      ...loadingBeforeSwapUuids,
      ...otherBeforeOrderUuids,
    ]);

    for (const uuid of allLoadedBeforeUuids) {
      if (!uuid) continue;

      if (
        unloadingAfterSwapUuids.has(uuid) ||
        trailerUuids.has(uuid) ||
        unassignedUuids.has(uuid)
      ) {
        const order = ordersMap.get(uuid);
        if (order) activeOrders.push(order);
      }
    }

    return activeOrders;
  }
}
